import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Card from "./Card";
import FantasyCardStats from "./FantasyCardStats";
import useFantasyTeams from "../hooks/useFantasyTeams";
import useTeamImages from "../hooks/useTeamImages";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/FantasyTeamView.css";

const FantasyTeamView = ({
  userID,
  event,
  season,
  parsedFantasyTeam,
  onClose,
  overlay = false,
}) => {
  const { fantasyTeams, loading, error } = useFantasyTeams(
    parsedFantasyTeam ? null : userID,
    parsedFantasyTeam ? null : event?._id,
    parsedFantasyTeam ? null : season?._id
  );
  const [team, setTeam] = useState([]);
  const [showOverlay, setShowOverlay] = useState(false); // State for overlay visibility
  const [overlayContent, setOverlayContent] = useState(null); // State for overlay content
  const navigate = useNavigate();

  useEffect(() => {
    if (parsedFantasyTeam) {
      // Use parsed fantasy team
      const updatedTeam = (parsedFantasyTeam.cards || []).map((card) => ({
        ...card,
        position: card.position,
      }));
      setTeam(updatedTeam);
    } else if (fantasyTeams && fantasyTeams.length > 0) {
      // Use fetched fantasy teams
      const updatedTeam = (fantasyTeams[0].cards || []).map((card) => ({
        ...card,
        position: card.position,
      }));
      setTeam(updatedTeam);
    }
  }, [parsedFantasyTeam, fantasyTeams]);

  const matches = useMemo(
    () => team.map((card) => card.matches).flat(),
    [team]
  );

  const images = useTeamImages(matches);

  const getPointsStyle = (point) => {
    return {
      color:
        point < 0
          ? "var(--lightred)"
          : point > 0
          ? "var(--lightgreen)"
          : "inherit",
    };
  };

  const cardStats = (fantasyCard) => {
    setOverlayContent(fantasyCard); // Set the card data to be displayed in the overlay
    setShowOverlay(true); // Show the overlay
  };

  const getNextMatch = (teamID) => {
    if (!teamID || teamID === undefined) return null;

    let team;
    if (
      parsedFantasyTeam &&
      parsedFantasyTeam.event &&
      parsedFantasyTeam.event.teamsInFantasy
    ) {
      team = parsedFantasyTeam.event.teamsInFantasy.find(
        (t) => t.team === teamID
      );
    } else if (event && event.teamsInFantasy) {
      team = event.teamsInFantasy.find((t) => t.team._id === teamID);
    }
    if (!team) {
      return null;
    }
    if (team.placement !== null && team.placement !== undefined) {
      return { placement: team.placement };
    } else if (team.nextMatch && team.nextMatch.length > 0) {
      return team.nextMatch[team.nextMatch.length - 1];
    } else {
      return null;
    }
  };

  const renderNextMatchOrPlacement = (nextMatch) => {
    if (!nextMatch) {
      return (
        <div className="event-points-box">
          <p className="card-points">TBD</p>
        </div>
      );
    }
    if (nextMatch.placement !== undefined) {
      return (
        <div className="event-points-box">
          <p className="card-points">{nextMatch.placement}</p>
        </div>
      );
    }
    if (nextMatch.startTime + 1000 * 60 * 60 < Date.now()) {
      return (
        <div className="event-points-box">
          <p className="card-points">No match scheduled</p>
        </div>
      );
    }
    if (
      !nextMatch.opponent ||
      !nextMatch.startTime ||
      !nextMatch.opponent.name
    ) {
      return (
        <div className="event-points-box">
          <p className="card-points">TBD</p>
        </div>
      );
    }
    return (
      <div className="event-points-box">
        <p className="card-points">
          {nextMatch.opponent.name} &nbsp;-&nbsp;{" "}
          {formatMatchDate(nextMatch.startTime)}
        </p>
      </div>
    );
  };

  const formatMatchDate = (unixTimestamp) => {
    const date = new Date(unixTimestamp);
    const options = {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    };
    const formattedDate = date.toLocaleDateString("da-DK", options).split(".");
    return `${formattedDate[0]}/${formattedDate[1]}:${formattedDate[2]}`;
  };

  return (
    <div
      className={`${overlay && "overlay"} ${
        !overlay || (showOverlay && "no-background")
      }`}
      onClick={onClose}
    >
      <div
        className="card-menu-container"
        style={{ height: "100%", marginTop: parsedFantasyTeam ? "0.5vw" : "0" }}
      >
        {parsedFantasyTeam ? (
          <div className="teamWinner-title-wrapper">
            <h1
              className="title-events hoverable"
              onClick={() =>
                navigate(`/event/${parsedFantasyTeam.event.hltv_id}`)
              }
            >
              {parsedFantasyTeam?.event?.name}
            </h1>
            <div className="teamWinner-info">
              {parsedFantasyTeam.placement && (
                <div className="teamWinner-box" style={{ width: "20vw" }}>
                  {"Placement: " + parsedFantasyTeam?.placement}
                </div>
              )}
              {parsedFantasyTeam.event?.isLive && (
                <p
                  className="live-eventpage"
                  style={{ margin: "0", color: "#fff" }}
                >
                  LIVE
                </p>
              )}
              <div className="teamWinner-box">
                {
                  <span
                    style={getPointsStyle(parsedFantasyTeam.points)}
                    className="points-value"
                  >
                    {parsedFantasyTeam.points.toFixed(0)}
                  </span>
                }{" "}
                <span className="points-label">Points</span>
              </div>
            </div>
          </div>
        ) : (
          <h1 className="title-events">
            <span
              className="title-username-clickable"
              onClick={(e) => {
                e.stopPropagation();
                const user = fantasyTeams && fantasyTeams[0]?.user;
                if (user && user.userNumber) {
                  navigate(`/profile/${user.userNumber}`);
                } else {
                  console.error("userNumber not found for user:", user);
                }
              }}
            >
              {fantasyTeams && fantasyTeams[0]?.user?.username}
            </span>
            {fantasyTeams && fantasyTeams[0]?.user?.username && (
              <>
                <span style={{ fontFamily: "fantasy" }}>'</span>s
              </>
            )}
            Fantasy Team
          </h1>
        )}
        {onClose && (
          <button className="overlay-close" onClick={onClose}>
            X
          </button>
        )}
        <div
          className="fantasyTeamView-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="event-fantasy-team-container overlay-content-center">
            {!parsedFantasyTeam && loading && <div className="loader"></div>}
            {!parsedFantasyTeam && error && <p className="error">{error}</p>}
            {team.length > 0 && (
              <div className="cards-container">
                {Array.from({ length: 5 }).map((_, index) => {
                  const card = team.find((c) => c.position === index + 1);
                  return (
                    <div key={index} className="event-fantasy-player">
                      {card ? (
                        <div onClick={() => cardStats(card)}>
                          {renderNextMatchOrPlacement(
                            getNextMatch(card.card.player.team._id)
                          )}
                          <div className="card-hoverable">
                            <Card card={card.card} />
                          </div>
                          <div className="event-points-box">
                            <p className="card-points">
                              <div>
                                <span style={getPointsStyle(card.points)}>
                                  {Math.round(card.points)}
                                </span>{" "}
                                Points
                              </div>
                            </p>
                          </div>
                        </div>
                      ) : (
                        <img
                          className="fantasy-player-image"
                          src={
                            "https://static.hltv.org/images/playerprofile/bodyshot/unknown.png"
                          }
                          alt="player"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!parsedFantasyTeam && !fantasyTeams && !loading && (
              <p className="no-cards">No best fantasy team found</p>
            )}
            {!parsedFantasyTeam && fantasyTeams && (
              <div className="event-teampoints-container">
                <div className="event-points-box total-points">
                  <p>
                    Total points:
                    <span style={getPointsStyle(fantasyTeams[0].points)}>
                      {" "}
                      {fantasyTeams[0].points.toFixed(2)}
                    </span>
                  </p>
                </div>
                <div className="event-points-box total-points">
                  <p>
                    Boost points:
                    <span style={getPointsStyle(fantasyTeams[0].boostPoints)}>
                      {" "}
                      {fantasyTeams[0].boostPoints.toFixed(2)}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Render the overlay */}
      {showOverlay && (
        <FantasyCardStats
          fantasyCard={overlayContent}
          onClose={() => setShowOverlay(false)}
          teamImages={images}
        />
      )}
    </div>
  );
};

export default FantasyTeamView;
